import { IconName } from '@shared/components/Icon';
import { bounceIn } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { Button } from './Button';
import { GhostButtonProps, PrimaryButtonProps, TertiaryButtonProps } from './ButtonProps';
import styles from './ClearButton.module.css';

export type ClearButtonProps =
  | PrimaryClearButtonProps
  | GhostClearButtonProps
  | TertiaryClearButtonProps;

export function ClearButton(props: ClearButtonProps) {
  if (props.variant === 'primary') {
    return <PrimaryClearButton {...props} />;
  } else if (props.variant === 'ghost') {
    return <GhostClearButton {...props} />;
  } else {
    return <TertiaryClearButton {...props} />;
  }
}

type PrimaryClearButtonProps = Omit<PrimaryButtonProps, 'variant' | 'danger' | 'arrow' | 'icon'> & {
  variant: 'primary';
  icon?: IconName;
};

function PrimaryClearButton({
  className,
  size = 'small',
  icon = 'exit-heavy',
  label = i18n('common.clear'),
  ...rest
}: PrimaryClearButtonProps) {
  return (
    <Button
      className={[styles.clearButton, bounceIn, className]}
      icon={icon}
      iconOnly
      label={label}
      size={size}
      {...rest}
    />
  );
}

type GhostClearButtonProps = Omit<
  GhostButtonProps,
  'variant' | 'danger' | 'engaged' | 'arrow' | 'icon'
> & {
  variant: 'ghost';
  icon?: IconName;
};

function GhostClearButton({
  className,
  size = 'small',
  icon = 'exit',
  label = i18n('common.clear'),
  ...rest
}: GhostClearButtonProps) {
  return (
    <Button
      className={[styles.clearButton, bounceIn, className]}
      icon={icon}
      iconOnly
      label={label}
      size={size}
      {...rest}
    />
  );
}

type TertiaryClearButtonProps = Omit<
  TertiaryButtonProps,
  'variant' | 'danger' | 'engaged' | 'arrow'
> & {
  variant?: 'tertiary';
  hasError?: boolean;
};

function TertiaryClearButton({
  className,
  hasError,
  size = 'small',
  icon = 'exit',
  label = i18n('common.clear'),
  ...rest
}: TertiaryClearButtonProps) {
  return (
    <Button
      className={[styles.clearButton, bounceIn, className, hasError && styles.hasError]}
      icon={icon}
      iconOnly
      label={label}
      size={size}
      {...rest}
    />
  );
}
